// Interface text in six languages (T-112): EN, JA, DE, ES, FR, IT. Every visible string goes
// through t(); the catalogues live in dev/locales/<locale>/web.json as ICU MessageFormat and are
// shared with the platform, which renders the e-mails from the sibling email.json.
// `node scripts/check-i18n.mjs` keeps the catalogues complete and the components free of
// hard-coded text. Terminology follows dev/locales/GLOSSARY.md (D-31, D-32).

import { IntlMessageFormat } from "intl-messageformat";
import { useSyncExternalStore } from "react";

import en from "../../locales/en/web.json";

export const LOCALES = ["en", "ja", "de", "es", "fr", "it"] as const;
export type Locale = (typeof LOCALES)[number];
export type MessageKey = keyof typeof en;
type Catalogue = Record<string, string>;

// Each language is its own chunk, fetched when it is chosen; English is always bundled as the
// fallback.
const loaders = import.meta.glob<{ default: Catalogue }>([
  "../../locales/*/web.json",
  "!../../locales/en/web.json",
]);

// Language names in their own language, so a visitor finds theirs whatever is selected.
export const LANGUAGE_NAMES: Record<Locale, string> = {
  en: "English",
  ja: "日本語",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  it: "Italiano",
};

const STORAGE_KEY = "f60.locale";
let current: Locale = "en";
let messages: Catalogue = en;
const formats = new Map<string, IntlMessageFormat>();
const listeners = new Set<() => void>();

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/// The best supported locale for a list of preferences such as navigator.languages.
export function negotiate(preferred: readonly string[]): Locale {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split(/[-_]/)[0];
    if (isLocale(base)) return base;
  }
  return "en";
}

function stored(): Locale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

export async function setLocale(locale: Locale): Promise<void> {
  if (locale !== "en") {
    const load = loaders[`../../locales/${locale}/web.json`];
    if (!load) return;
    messages = (await load()).default;
  } else {
    messages = en;
  }
  current = locale;
  formats.clear();
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Private windows may refuse storage; the choice then lasts for this page only.
  }
  for (const listener of listeners) listener();
}

/// Called once before the first render: the last choice on this device, else the browser's.
export async function initLocale(): Promise<void> {
  await setLocale(stored() ?? negotiate(navigator.languages ?? [navigator.language]));
}

export function locale(): Locale {
  return current;
}

export function useLocale(): Locale {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
  );
}

export function hasMessage(key: string): key is MessageKey {
  return key in en;
}

export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const message = messages[key] ?? en[key];
  let format = formats.get(key);
  if (!format) {
    try {
      format = new IntlMessageFormat(message, current);
    } catch {
      return en[key];
    }
    formats.set(key, format);
  }
  const result = format.format(vars);
  return typeof result === "string" ? result : String(result);
}

export function formatDate(value: string | Date, options?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(current, options ?? { day: "numeric", month: "short", year: "numeric" }).format(
    typeof value === "string" ? new Date(value) : value,
  );
}

/// A language's name in the reader's language, for example "German" for "de".
export function languageName(code: string): string {
  try {
    return new Intl.DisplayNames([current], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(current, options).format(value);
}
